interface SquareError {
	code?: string
	detail?: string
}

const SQUARE_ERROR_CODES: Record<string, string> = {
	APPLE_TTP_PIN_TOKEN:
		"The payment was declined by the card issuer during an Apple Tap to Pay (TTP) transaction with a request for the card's PIN.",
	CARD_EXPIRED: "The card issuer declined the request because the card is expired.",
	INVALID_EXPIRATION:
		"The expiration date for the payment card is invalid. For example, it indicates a date in the past.",
	INVALID_EXPIRATION_YEAR:
		"The expiration year for the payment card is invalid. For example, it indicates a year in the past or contains invalid characters.",
	INVALID_EXPIRATION_DATE:
		"The expiration date for the payment card is invalid. For example, it contains invalid characters.",
	UNSUPPORTED_CARD_BRAND: "The credit card provided is not from a supported issuer.",
	INVALID_CARD: "The credit card cannot be validated based on the provided details.",
	GENERIC_DECLINE:
		"Square received a decline without any additional information. If the payment information seems correct, the buyer can contact their issuer to ask for more information.",
	CVV_FAILURE: "The card issuer declined the request because the CVV value is invalid.",
	ADDRESS_VERIFICATION_FAILURE:
		"The card issuer declined the request because the postal code is invalid.",
	INVALID_ACCOUNT: "The issuer was not able to locate the account on record.",
	CURRENCY_MISMATCH:
		"The currency associated with the payment is not valid for the provided funding source.",
	INSUFFICIENT_FUNDS: "The funding source has insufficient funds to cover the payment.",
	CARDHOLDER_INSUFFICIENT_PERMISSIONS:
		"The card issuer has declined the transaction due to restrictions on where the card can be used.",
	TRANSACTION_LIMIT:
		"The card issuer has determined the payment amount is either too high or too low.",
	VOICE_FAILURE:
		"The card issuer declined the request because the issuer requires voice authorization from the cardholder.",
	PAN_FAILURE: "The specified card number is invalid.",
	EXPIRATION_FAILURE:
		"The card expiration date is either invalid or indicates that the card is expired.",
	CARD_NOT_SUPPORTED:
		"The card is not supported either in the geographic region or by the merchant category code (MCC).",
	INVALID_PIN: "The card issuer declined the request because the PIN is invalid.",
	MISSING_PIN: "The payment is missing a required PIN.",
	INVALID_POSTAL_CODE: "The postal code is incorrectly formatted.",
	ACCOUNT_UNUSABLE: "The account provided cannot carry out transactions.",
	BUYER_REFUSED_PAYMENT: "Bank account rejected or was not authorized for the payment.",
	AMOUNT_TOO_HIGH: "The requested payment amount is too high for the provided payment source.",
	REFUND_DECLINED: "Request failed - The card issuer declined the refund.",
	INVALID_CARD_DATA: "Generic error - the provided card data is invalid.",
	INVALID_EMAIL_ADDRESS: "The provided email address is invalid.",
	INVALID_PHONE_NUMBER: "The provided phone number is invalid.",
	CARD_PRESENCE_REQUIRED: "The transaction requires that a card be present.",
	CARD_MISMATCH: "The provided card does not match what is expected.",
	CARD_DECLINED: "The card was declined.",
	VERIFY_CVV_FAILURE: "The CVV could not be verified.",
	VERIFY_AVS_FAILURE: "The AVS could not be verified.",
	CARD_DECLINED_CALL_ISSUER:
		"The payment card was declined with a request for the card holder to call the issuer.",
	CARD_DECLINED_VERIFICATION_REQUIRED:
		"The payment card was declined with a request for additional verification.",
	BAD_EXPIRATION: "The card expiration date is either missing or incorrectly formatted.",
	CHIP_INSERTION_REQUIRED: "The card issuer requires that the card be read using a chip reader.",
	ALLOWABLE_PIN_TRIES_EXCEEDED:
		"The card has exhausted its available pin entry retries set by the card issuer.",
	RESERVATION_DECLINED: "The card issuer declined the refund.",
}

const DEFAULT_SQUARE_ERROR_MESSAGE =
	"An unexpected error occurred while processing your payment. Please try again or contact support if the problem persists."

const getCustomerErrorLayerMessage = (errorMessage: string) =>
	`Error: ${errorMessage} Please have a look to your order and try again.`

export const handleSquareErrorResponse = (errorData: {
	error?: string
	payment?: string
}): string => {
	if (!errorData.error) {
		return errorData.payment || DEFAULT_SQUARE_ERROR_MESSAGE
	}

	try {
		const parsedErrorString = parseSquareErrorString(errorData.error)
		const errorArray = JSON.parse(parsedErrorString) as SquareError[]
		const firstError = errorArray[0]

		if (firstError?.code) {
			return getSquareErrorMessage(firstError.code)
		}

		return errorArray
			.map((err) => err.detail)
			.filter(Boolean)
			.join(", ")
	} catch (parseError) {
		const errorCode = extractErrorCodeFromString(errorData.error)
		return errorCode ? getSquareErrorMessage(errorCode) : DEFAULT_SQUARE_ERROR_MESSAGE
	}
}

const getSquareErrorMessage = (errorCode: string): string => {
	return (
		getCustomerErrorLayerMessage(SQUARE_ERROR_CODES[errorCode]) || DEFAULT_SQUARE_ERROR_MESSAGE
	)
}

const parseSquareErrorString = (errorString: string): string => {
	return errorString
		.replace(/'/g, '"')
		.replace(/"Authorization error: "([^"]+)""/g, "\"Authorization error: '$1'\"")
}

const extractErrorCodeFromString = (errorString: string): string | null => {
	const errorMatches = errorString.match(/"code": "([^"]+)"/g)
	if (!errorMatches) return null

	return errorMatches[0].split('"code": "')[1].replace('"', "")
}
